import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

import { INITIAL_DEMO_BUDGETS } from '../lib/demoData';

const BudgetContext = createContext();

export const useBudgets = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudgets must be used within a BudgetProvider');
  }
  return context;
};

const DEMO_BUDGETS_KEY = 'spend_easy_demo_budgets';

const loadDemoBudgets = () => {
  try {
    const saved = localStorage.getItem(DEMO_BUDGETS_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error('Error loading demo budgets:', e);
  }
  return INITIAL_DEMO_BUDGETS;
};

const saveDemoBudgets = (b) => {
  try {
    localStorage.setItem(DEMO_BUDGETS_KEY, JSON.stringify(b));
  } catch (e) {
    console.error('Error saving demo budgets:', e);
  }
};

export const BudgetProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [budgets, setBudgets] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setBudgets({});
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setBudgets(loadDemoBudgets());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const budgetDocRef = doc(db, 'users', currentUser.uid, 'settings', 'budgets');
    const unsubscribe = onSnapshot(budgetDocRef, (docSnap) => {
      setBudgets(docSnap.exists() ? (docSnap.data().categoryBudgets || {}) : {});
      setLoading(false);
    }, (error) => {
      console.error('Error fetching budgets:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const setBudget = async (category, amount) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = { ...budgets, [category]: parseFloat(amount) };
      setBudgets(updated);
      saveDemoBudgets(updated);
      return;
    }

    const budgetDocRef = doc(db, 'users', currentUser.uid, 'settings', 'budgets');
    await setDoc(budgetDocRef, {
      categoryBudgets: { ...budgets, [category]: parseFloat(amount) },
      updatedAt: serverTimestamp()
    }, { merge: true });
  };

  const removeBudget = async (category) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = { ...budgets };
      delete updated[category];
      setBudgets(updated);
      saveDemoBudgets(updated);
      return;
    }

    const budgetDocRef = doc(db, 'users', currentUser.uid, 'settings', 'budgets');
    const newBudgets = { ...budgets };
    delete newBudgets[category];
    await setDoc(budgetDocRef, { categoryBudgets: newBudgets, updatedAt: serverTimestamp() }, { merge: true });
  };

  const value = { budgets, setBudget, removeBudget, loading };

  return (
    <BudgetContext.Provider value={value}>
      {children}
    </BudgetContext.Provider>
  );
};
